import api, { apiErrorMessage } from './api';

/**
 * Posteingang „Neu für dich“ (Marcel 2026-10-24): persönliche Pushes je Nutzer
 * (Chat, Anmeldungen, Ranglisten-Anfragen, Mitgliedschaften, Follower, …).
 * GET /api/me/inbox?limit&unreadOnly → { items, unreadCount, chatUnreadCount }
 * POST /api/me/inbox/read { ids | all } → { updated, unreadCount }
 * Ziel-Seite je Eintrag: utils/pushRoute.ts (pushTargetFor(item.data)).
 */

export interface InboxItem {
  id: string;
  flowKey: string | null;
  title: string | null;
  body: string | null;
  data: Record<string, unknown> | null;
  createdAt: number | null;
  readAt: number | null;
  unread: boolean;
}

export interface InboxList {
  items: InboxItem[];
  unreadCount: number;
  chatUnreadCount: number;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** PURE + defensiv: Backend-Eintrag → InboxItem (ohne id → null) */
export function parseInboxItem(raw: any): InboxItem | null {
  const id = str(raw?.id);
  if (!id) return null;
  const readAt = num(raw.readAt);
  return {
    id,
    flowKey: str(raw.flowKey),
    title: str(raw.title),
    body: str(raw.body),
    data: raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : null,
    createdAt: num(raw.createdAt),
    readAt,
    unread: typeof raw.unread === 'boolean' ? raw.unread : readAt == null,
  };
}

/** PURE + defensiv: Liste (fehlende Felder → leer/0) */
export function parseInboxList(raw: any): InboxList {
  const items = (Array.isArray(raw?.items) ? raw.items : []).map(parseInboxItem).filter((i: InboxItem | null): i is InboxItem => i != null);
  return { items, unreadCount: Math.max(0, num(raw?.unreadCount) ?? 0), chatUnreadCount: Math.max(0, num(raw?.chatUnreadCount) ?? 0) };
}

/** PURE: Einträge lokal als gelesen markieren (ids oder alle) */
export function markItemsRead(items: InboxItem[], ids: string[] | 'all', now = Date.now()): InboxItem[] {
  return items.map((i) => (ids === 'all' || ids.includes(i.id) ? { ...i, unread: false, readAt: i.readAt ?? now } : i));
}

class UserInboxService {
  async list(options: { limit?: number; unreadOnly?: boolean } = {}): Promise<InboxList> {
    const params: Record<string, string> = {};
    if (options.limit) params.limit = String(options.limit);
    if (options.unreadOnly) params.unreadOnly = '1';
    const res = await api.get('/api/me/inbox', { params });
    return parseInboxList(res?.data);
  }

  async markRead(ids: string[] | 'all'): Promise<{ updated: number; unreadCount: number }> {
    const body = ids === 'all' ? { all: true } : { ids };
    const res = await api.post('/api/me/inbox/read', body);
    return { updated: num(res?.data?.updated) ?? 0, unreadCount: Math.max(0, num(res?.data?.unreadCount) ?? 0) };
  }

  getErrorMessage(err: unknown, fallback: string): string { return apiErrorMessage(err, fallback); }
}

const userInboxService = new UserInboxService();
export default userInboxService;
